(function () {
  'use strict';

  const ALIAS_GROUPS = {
    zzz: ['zzz', 'zenless zone zero'],
    cassette: ['cassette', 'tape'],
    tape: ['tape', 'cassette'],
    badge: ['badge', 'pin'],
    pin: ['pin', 'badge'],
    thermos: ['thermos', 'insulated bottle'],
    bottle: ['bottle', 'thermos']
  };

  const PREDICTIVE_CANONICAL = {
    zzz: 'zenless zone zero',
    tape: 'cassette',
    pin: 'badge'
  };

  const FIELD_BY_SCOPE = {
    title: 'title',
    tag: 'tag',
    variant: 'variants.title'
  };

  function tokenize(value) {
    return String(value || '').match(/"[^"]+"|\S+/g) || [];
  }

  function cleanValue(value) {
    return String(value || '')
      .replace(/^"|"$/g, '')
      .replace(/["\\]/g, '')
      .replace(/([:()])/g, '\\$1')
      .trim();
  }

  function formatSearchValue(value) {
    const clean = cleanValue(value);
    if (!clean) return '';
    return /\s/.test(clean) ? '"' + clean + '"' : clean;
  }

  function buildSearchQuery(rawValue, scope) {
    const terms = tokenize(rawValue);
    const field = FIELD_BY_SCOPE[scope] || '';

    if (!field) {
      return terms.map(formatSearchValue).filter(Boolean).join(' ');
    }

    return terms.map(function (term) {
      const clean = cleanValue(term);
      if (!clean) return '';

      const aliases = ALIAS_GROUPS[clean.toLowerCase()] || [clean];
      const clauses = aliases.map(function (alias) {
        const value = formatSearchValue(alias);
        if (!value) return '';

        const wildcard = value.indexOf('"') === -1 && value.length > 1 ? '*' : '';
        return field + ':' + value + wildcard;
      }).filter(Boolean);

      return clauses.length > 1 ? '(' + clauses.join(' OR ') + ')' : clauses[0];
    }).filter(Boolean).join(' ');
  }

  function friendlyFromQuery(query) {
    const value = String(query || '');
    if (!/\b(title|tag|variants\.title):/i.test(value)) return value;

    const words = value
      .replace(/(?:title|tag|variants\.title):/gi, '')
      .replace(/[()*"]/g, ' ')
      .replace(/\bOR\b/gi, ' ')
      .replace(/\\([:()])/g, '$1')
      .split(/\s+/)
      .filter(Boolean);

    return Array.from(new Set(words.map(function (word) { return word.toLowerCase(); }))).join(' ');
  }

  function currentFriendlyValue() {
    const params = new URLSearchParams(window.location.search);
    return params.get('kss_q') || friendlyFromQuery(params.get('q') || '');
  }

  function currentScope() {
    const params = new URLSearchParams(window.location.search);
    const scope = params.get('kss_scope') || 'all';
    return FIELD_BY_SCOPE[scope] ? scope : 'all';
  }

  function buildSearchUrl(form, rawValue, scope) {
    const url = new URL(form.action, window.location.origin);
    url.searchParams.set('q', buildSearchQuery(rawValue, scope));
    url.searchParams.set('kss_q', rawValue);
    url.searchParams.set('kss_scope', scope);
    url.searchParams.set('type', 'product');
    url.searchParams.set('options[prefix]', 'last');
    url.searchParams.set('options[unavailable_products]', 'last');
    return url;
  }

  function syncSearchForm(form) {
    const input = form.querySelector('[data-kss-search-input]');
    const rawInput = form.querySelector('[data-kss-search-raw]');
    const scopeInput = form.querySelector('[data-kss-search-scope-input]');
    const scopeSelect = form.querySelector('[data-kss-search-scope-select]');
    if (!input) return;

    const friendly = currentFriendlyValue();
    const scope = currentScope();

    if (friendly) {
      input.value = friendly;
      if (rawInput) rawInput.value = friendly;
    }

    if (scopeInput) scopeInput.value = scope;
    if (scopeSelect) scopeSelect.value = scope;

    document.querySelectorAll('[data-kss-search-summary-term]').forEach(function (node) {
      if (friendly) node.textContent = friendly;
    });
  }

  function prepareSearchSubmit(form, event) {
    const input = form.querySelector('[data-kss-search-input]');
    const queryInput = form.querySelector('[data-kss-search-query]');
    const rawInput = form.querySelector('[data-kss-search-raw]');
    const scopeInput = form.querySelector('[data-kss-search-scope-input]');
    const scopeSelect = form.querySelector('[data-kss-search-scope-select]');
    if (!input || !queryInput) return;

    const raw = input.value.trim().replace(/\s+/g, ' ');
    if (!raw) {
      event.preventDefault();
      input.focus();
      return;
    }

    const scope = scopeSelect ? scopeSelect.value : (form.dataset.kssSearchScope || 'all');
    queryInput.value = buildSearchQuery(raw, scope);
    if (rawInput) rawInput.value = raw;
    if (scopeInput) scopeInput.value = scope;
  }

  function predictiveQuery(rawValue) {
    return tokenize(rawValue).map(function (term) {
      const clean = cleanValue(term);
      return PREDICTIVE_CANONICAL[clean.toLowerCase()] || clean;
    }).filter(Boolean).join(' ');
  }

  function safeUrl(value) {
    try {
      const url = new URL(String(value || ''), window.location.origin);
      if (url.protocol !== 'http:' && url.protocol !== 'https:') return '#';
      return url.href;
    } catch (error) {
      return '#';
    }
  }

  function formatPrice(value, currency) {
    const number = Number(value);
    if (!Number.isFinite(number)) return '';

    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency || 'USD',
        maximumFractionDigits: number % 1 === 0 ? 0 : 2
      }).format(number);
    } catch (error) {
      return (currency || '') + ' ' + number.toFixed(2);
    }
  }

  function createTextElement(tag, className, textValue) {
    const element = document.createElement(tag);
    if (className) element.className = className;
    element.textContent = String(textValue || '');
    return element;
  }

  class KSSPredictiveSearch {
    constructor(form) {
      this.form = form;
      this.input = form.querySelector('[data-kss-search-input]');
      this.panel = form.querySelector('[data-kss-predictive]');
      this.trending = form.querySelector('[data-kss-predictive-trending]');
      this.results = form.querySelector('[data-kss-predictive-results]');
      this.status = form.querySelector('[data-kss-predictive-status]');
      this.productList = form.querySelector('[data-kss-predictive-products]');
      this.queryWrap = form.querySelector('[data-kss-predictive-queries]');
      this.queryList = form.querySelector('[data-kss-predictive-query-list]');
      this.empty = form.querySelector('[data-kss-predictive-empty]');
      this.viewAll = form.querySelector('[data-kss-predictive-all]');
      this.viewAllTerm = form.querySelector('[data-kss-predictive-term]');
      this.closeButton = form.querySelector('[data-kss-predictive-close]');
      this.currency = form.dataset.kssSearchCurrency || 'USD';
      this.cache = new Map();
      this.controller = null;
      this.timer = null;
      this.activeIndex = -1;
      this.requestNumber = 0;

      if (!this.input || !this.panel || !this.productList) return;
      this.bind();
    }

    bind() {
      this.input.addEventListener('focus', () => this.handleInput(true));
      this.input.addEventListener('input', () => this.handleInput(false));
      this.input.addEventListener('keydown', (event) => this.handleKeydown(event));

      this.form.addEventListener('focusout', () => {
        window.setTimeout(() => {
          if (!this.form.contains(document.activeElement)) this.close();
        }, 120);
      });

      this.form.addEventListener('submit', () => this.close());
      if (this.closeButton) this.closeButton.addEventListener('click', () => {
        this.close();
        this.input.focus();
      });

      this.panel.addEventListener('mousemove', (event) => {
        const option = event.target.closest('[data-kss-predictive-option]');
        if (!option) return;
        const options = this.options();
        this.setActive(options.indexOf(option));
      });
    }

    open() {
      this.panel.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add('kss-search-open');
    }

    close() {
      window.clearTimeout(this.timer);
      this.panel.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      this.activeIndex = -1;
      window.requestAnimationFrame(function () {
        const hasOpenSearch = Array.from(document.querySelectorAll('[data-kss-predictive]')).some(function (panel) {
          return !panel.hidden;
        });
        document.documentElement.classList.toggle('kss-search-open', hasOpenSearch);
      });
    }

    showTrending() {
      if (this.trending) this.trending.hidden = false;
      if (this.results) this.results.hidden = true;
      if (this.status) this.status.textContent = 'Type at least 2 characters for live results.';
      this.open();
    }

    handleInput(immediate) {
      const raw = this.input.value.trim();
      this.activeIndex = -1;
      this.input.removeAttribute('aria-activedescendant');
      window.clearTimeout(this.timer);

      if (raw.length < 2) {
        if (this.controller) this.controller.abort();
        this.showTrending();
        return;
      }

      this.open();
      if (this.trending) this.trending.hidden = true;
      if (this.results) this.results.hidden = true;
      if (this.status) this.status.textContent = 'Searching…';
      this.timer = window.setTimeout(() => this.fetch(raw), immediate ? 0 : 220);
    }

    async fetch(raw) {
      const query = predictiveQuery(raw);
      const cacheKey = query.toLowerCase();

      if (this.cache.has(cacheKey)) {
        this.render(this.cache.get(cacheKey), raw);
        return;
      }

      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      const requestNumber = ++this.requestNumber;

      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root)
        || this.form.dataset.kssSearchRoot
        || '/';
      const url = new URL(root + 'search/suggest.json', window.location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('resources[type]', 'query,product');
      url.searchParams.set('resources[limit]', '6');
      url.searchParams.set('resources[limit_scope]', 'each');
      url.searchParams.set('resources[options][unavailable_products]', 'last');
      url.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,vendor,tag');

      try {
        const response = await window.fetch(url.toString(), {
          headers: { Accept: 'application/json' },
          signal: this.controller.signal
        });

        if (!response.ok) throw new Error('Predictive search request failed');
        const data = await response.json();
        if (requestNumber !== this.requestNumber) return;
        this.cache.set(cacheKey, data);
        this.render(data, raw);
      } catch (error) {
        if (error.name === 'AbortError') return;
        if (this.status) this.status.textContent = 'Live suggestions are unavailable. Press Enter to search.';
        if (this.results) this.results.hidden = true;
        this.open();
      }
    }

    render(data, raw) {
      const resourceResults = data && data.resources && data.resources.results
        ? data.resources.results
        : {};
      const products = Array.isArray(resourceResults.products) ? resourceResults.products : [];
      const queries = Array.isArray(resourceResults.queries) ? resourceResults.queries : [];

      this.productList.replaceChildren();
      if (this.queryList) this.queryList.replaceChildren();

      products.forEach((product, index) => {
        this.productList.appendChild(this.productOption(product, index));
      });

      if (this.queryList && this.queryWrap) {
        queries.slice(0, 4).forEach((query, index) => {
          const term = String(query.text || '').trim();
          if (!term) return;
          const link = createTextElement('a', '', term);
          link.href = buildSearchUrl(this.form, term, 'all').href;
          link.id = this.panel.id + '-query-' + index;
          link.setAttribute('role', 'option');
          link.setAttribute('aria-selected', 'false');
          link.setAttribute('data-kss-predictive-option', '');
          this.queryList.appendChild(link);
        });
        this.queryWrap.hidden = this.queryList.children.length === 0;
      }

      if (this.empty) this.empty.hidden = products.length > 0;
      if (this.viewAll) {
        const scopeSelect = this.form.querySelector('[data-kss-search-scope-select]');
        const scope = scopeSelect ? scopeSelect.value : (this.form.dataset.kssSearchScope || 'all');
        this.viewAll.href = buildSearchUrl(this.form, raw, scope).href;
      }
      if (this.viewAllTerm) this.viewAllTerm.textContent = raw;
      if (this.trending) this.trending.hidden = true;
      if (this.results) this.results.hidden = false;
      if (this.status) {
        this.status.textContent = products.length
          ? products.length + ' product suggestion' + (products.length === 1 ? '' : 's')
          : 'No instant product match';
      }

      this.activeIndex = -1;
      this.open();
    }

    productOption(product, index) {
      const item = document.createElement('li');
      item.className = 'kss-predictive-product';

      const link = document.createElement('a');
      link.className = 'kss-predictive-product__link';
      link.href = safeUrl(product.url);
      link.id = this.panel.id + '-product-' + index;
      link.setAttribute('role', 'option');
      link.setAttribute('aria-selected', 'false');
      link.setAttribute('data-kss-predictive-option', '');

      const imageUrl = product.image
        || (product.featured_image && product.featured_image.url)
        || '';
      if (imageUrl) {
        const image = document.createElement('img');
        image.className = 'kss-predictive-product__image';
        image.src = safeUrl(imageUrl);
        image.alt = product.featured_image && product.featured_image.alt
          ? String(product.featured_image.alt)
          : '';
        image.width = 72;
        image.height = 72;
        image.loading = 'lazy';
        link.appendChild(image);
      } else {
        const placeholder = document.createElement('span');
        placeholder.className = 'kss-predictive-product__image kss-predictive-product__placeholder';
        placeholder.setAttribute('aria-hidden', 'true');
        link.appendChild(placeholder);
      }

      const copy = document.createElement('span');
      copy.className = 'kss-predictive-product__copy';
      copy.appendChild(createTextElement('span', 'kss-predictive-product__meta', product.vendor || product.type || 'KSS ANIME'));
      copy.appendChild(createTextElement('strong', 'kss-predictive-product__title', product.title));

      const price = formatPrice(product.price, this.currency);
      if (price) {
        const priceText = product.available === false ? price + ' · Sold out' : price;
        copy.appendChild(createTextElement('span', 'kss-predictive-product__price', priceText));
      }

      link.appendChild(copy);
      const arrow = createTextElement('span', 'kss-predictive-product__arrow', '→');
      arrow.setAttribute('aria-hidden', 'true');
      link.appendChild(arrow);
      item.appendChild(link);
      return item;
    }

    options() {
      return Array.from(this.panel.querySelectorAll('[data-kss-predictive-option]')).filter(function (option) {
        return option.offsetParent !== null;
      });
    }

    setActive(index) {
      const options = this.options();
      if (!options.length) return;

      this.activeIndex = (index + options.length) % options.length;
      options.forEach((option, optionIndex) => {
        const active = optionIndex === this.activeIndex;
        option.classList.toggle('is-active', active);
        option.setAttribute('aria-selected', active ? 'true' : 'false');
      });

      const selected = options[this.activeIndex];
      this.input.setAttribute('aria-activedescendant', selected.id);
      selected.scrollIntoView({ block: 'nearest' });
    }

    handleKeydown(event) {
      if (event.key === 'Escape') {
        if (!this.panel.hidden) {
          event.preventDefault();
          this.close();
        }
        return;
      }

      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp' && event.key !== 'Enter') return;

      if (this.panel.hidden && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
        event.preventDefault();
        this.handleInput(true);
        return;
      }

      const options = this.options();
      if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && options.length) {
        event.preventDefault();
        const direction = event.key === 'ArrowDown' ? 1 : -1;
        this.setActive(this.activeIndex + direction);
      } else if (event.key === 'Enter' && this.activeIndex >= 0 && options[this.activeIndex]) {
        event.preventDefault();
        options[this.activeIndex].click();
      }
    }
  }

  function initSearch() {
    document.querySelectorAll('[data-kss-search-form]:not([data-kss-search-ready])').forEach(function (form) {
      form.setAttribute('data-kss-search-ready', 'true');
      syncSearchForm(form);
      form.addEventListener('submit', function (event) {
        prepareSearchSubmit(form, event);
      });

      form.querySelectorAll('[data-kss-search-auto-submit]').forEach(function (select) {
        select.addEventListener('change', function () {
          if (typeof form.requestSubmit === 'function') form.requestSubmit();
          else form.submit();
        });
      });

      if (form.hasAttribute('data-kss-predictive-enabled')) new KSSPredictiveSearch(form);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSearch);
  } else {
    initSearch();
  }

  document.addEventListener('shopify:section:load', initSearch);
})();
